/**
 * Config Editor Page
 *
 * Visual config editor integrating the ConfigEditor component:
 * - Code/form view toggle via ConfigEditor
 * - Form builders for all 5 config types
 * - Draft management with auto-save and collaborative locking
 * - Save/publish controls with promotion workflow
 * - Dirty tracking via JSON comparison
 * - Version history panel with rollback support
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  EligibilityConfig,
  RankingConfig,
  BrandingConfig,
  JourneyConfig,
} from "@tatvex/config";
import {
  ChevronRight,
  Loader2,
  AlertCircle,
  RefreshCw,
  Save,
  Upload,
  Download,
  Home,
  History,
  Clock,
  ExternalLink,
  ShieldCheck,
} from "lucide-react";
import type { ReactElement } from "react";
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";

import { apiRequest } from "../api/client";
import { ConfigEditor } from "../components/form-builder/ConfigEditor";
import type { SchemaFormValue } from "../components/form-builder/types";
import { PromotionDialog, VersionHistoryPanel, RollbackDialog } from "../components/promotion";
import type { PromotionMode, ChangedFileSummary } from "../components/promotion/types";
import { LockIndicatorCompact } from "../components/status/LockIndicator";
import { SaveStatusCompact } from "../components/status/SaveStatus";
import { createApprovalApiClient, useApprovalQueue } from "../hooks/use-approval-api";
import type {
  ApprovalConfigType,
  SourceEnvironment,
  TargetEnvironment,
} from "../hooks/use-approval-api";
import {
  createConfigApiClient,
  useConfigValidation,
  useConfigDiff,
  useVersionHistory,
  usePromotion,
  useRollback,
} from "../hooks/use-config-api";
import type { ConfigApiClient } from "../hooks/use-config-api";
import { useDraftManagement, createLockApiClient } from "../hooks/use-draft-management";
import { useAuthStore } from "../store/authStore";
import { CONFIG_TYPE_LABELS, type ConfigType } from "../types";

interface ConfigContent {
  readonly content: string;
  readonly version: number;
  readonly status: "draft" | "live" | "archived";
  readonly checksum: string;
  readonly lastModified?: string;
}

/** Config type union accepted by ConfigEditor */
type EditorConfigType = "eligibility" | "ranking" | "branding" | "journey" | "schema";

/** Config value union matching ConfigEditor's generic constraint */
type ConfigValue =
  | EligibilityConfig
  | RankingConfig
  | BrandingConfig
  | JourneyConfig
  | SchemaFormValue;

/** ConfigType for API hooks */
type ApiConfigType = "schema" | "eligibility" | "ranking" | "journey" | "branding";

// ============================================================================
// Helpers
// ============================================================================

/** Get API base URL from environment */
function getApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL as string | undefined;
  return envUrl ?? "http://localhost:8787";
}

/** Create config API client for promotion hooks */
function getApiClient(): ConfigApiClient {
  return createConfigApiClient({
    baseUrl: "",
    getAuthToken: () => {
      try {
        const stored = localStorage.getItem("tatvex_auth");
        if (stored) {
          const parsed = JSON.parse(stored) as { token?: string };
          return parsed.token ?? null;
        }
      } catch {
        // ignore parse errors
      }
      return null;
    },
  });
}

// ============================================================================
// Main Component
// ============================================================================

export function ConfigEditorPage(): ReactElement {
  const { verticalKey, configType } = useParams<{
    verticalKey: string;
    configType: ConfigType;
  }>();
  const queryClient = useQueryClient();

  const [editedValue, setEditedValue] = useState<ConfigValue | null>(null);
  const [showPromotionDialog, setShowPromotionDialog] = useState(false);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [showRollbackDialog, setShowRollbackDialog] = useState(false);
  const [rollbackTargetVersion, setRollbackTargetVersion] = useState<number | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // API client for promotion hooks
  const apiClient = useMemo(getApiClient, []);
  const apiConfigType = (configType ?? "schema") as ApiConfigType;
  const apiVerticalKey = verticalKey ?? "";

  // Promotion workflow hooks
  const {
    validation: validationResult,
    isValidating,
    validate,
  } = useConfigValidation(apiClient, apiVerticalKey, apiConfigType);

  const {
    diff: diffResult,
    liveVersion: diffLiveVersion,
    draftVersion: diffDraftVersion,
    refresh: refreshDiff,
  } = useConfigDiff(apiClient, apiVerticalKey, apiConfigType);

  const { isPromoting, promote } = usePromotion(apiClient, apiVerticalKey, apiConfigType);

  const {
    entries: historyEntries,
    pagination: historyPagination,
    isLoading: isLoadingHistory,
    error: historyError,
    refresh: refreshHistory,
    loadMore: loadMoreHistory,
  } = useVersionHistory(apiClient, apiVerticalKey, apiConfigType);

  const { isRollingBack, rollback } = useRollback(apiClient, apiVerticalKey, apiConfigType);

  // ============================================================================
  // Approval flow — role-based promotion
  // ============================================================================

  const { hasCapability, environment: currentEnvironment } = useAuthStore();
  const canPromoteDirectly = hasCapability("config:promote");
  const canWrite = hasCapability("config:write");

  /** Determine promotion dialog mode based on user capabilities */
  const promotionMode: PromotionMode = canPromoteDirectly ? "direct" : "approval";

  /** Derive target environment from current environment.
   * Promotion from prod is not supported — only dev->staging and staging->prod. */
  const canPromoteFromEnv = currentEnvironment !== "prod";
  const targetEnvironment: TargetEnvironment =
    currentEnvironment === "staging" ? "prod" : "staging";

  /** Approval API client for requesting promotions */
  const approvalApiClient = useMemo(
    () =>
      createApprovalApiClient({
        baseUrl: "",
        getAuthToken: () => {
          try {
            const stored = localStorage.getItem("tatvex_auth");
            if (stored) {
              const parsed = JSON.parse(stored) as { token?: string };
              return parsed.token ?? null;
            }
          } catch {
            // ignore parse errors
          }
          return null;
        },
      }),
    []
  );

  /** Check for pending approvals for this config */
  const { promotions: pendingPromotions, isLoading: isLoadingPending } = useApprovalQueue(
    approvalApiClient,
    {
      status: "pending",
      verticalKey: apiVerticalKey,
    }
  );

  /** Whether there is a pending approval for this specific config type */
  const hasPendingApproval = useMemo(
    () =>
      pendingPromotions.some(
        (p) => p.configTypes.includes(apiConfigType) && p.verticalKey === apiVerticalKey
      ),
    [pendingPromotions, apiConfigType, apiVerticalKey]
  );

  // Determine live version from history entries
  const liveVersion = useMemo(() => {
    const liveEntry = historyEntries.find((e) => e.status === "live");
    return liveEntry?.version ?? null;
  }, [historyEntries]);

  // Ref to always provide latest editedValue to the draft management hook
  const editedValueRef = useRef<ConfigValue | null>(null);
  editedValueRef.current = editedValue;

  // Auth token accessor
  const getAuthToken = useCallback((): string | null => {
    return useAuthStore.getState().token ?? null;
  }, []);

  // Create API clients for draft management (memoized)
  const apiBaseUrl = getApiBaseUrl();

  const configApiClient = useMemo(
    () => createConfigApiClient({ baseUrl: apiBaseUrl, getAuthToken }),
    [apiBaseUrl, getAuthToken]
  );

  const lockApiClient = useMemo(
    () => createLockApiClient({ baseUrl: apiBaseUrl, getAuthToken }),
    [apiBaseUrl, getAuthToken]
  );

  // Content accessor for the auto-save hook
  const getContent = useCallback((): unknown => {
    return editedValueRef.current;
  }, []);

  // Derive file path from config type (matches lock API convention)
  const filePath = configType ? `${configType}.json` : "";

  // Draft management hook — auto-save + collaborative locking
  const {
    saveStatus: draftSaveStatus,
    isDirty: draftIsDirty,
    markDirty,
    saveNow,
    lastSavedAt,
    saveError,
    lockStatus,
    heldByInfo,
    canEdit,
    isReadOnly,
  } = useDraftManagement(
    configApiClient,
    lockApiClient,
    verticalKey ?? "",
    (configType ?? "schema") as EditorConfigType,
    filePath,
    getContent,
    {
      intervalMs: 30000,
      autoAcquire: true,
      enabled: !!verticalKey && !!configType,
    }
  );

  // Fetch config content
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["config", verticalKey, configType],
    queryFn: async () => {
      const response = await apiRequest<ConfigContent>(
        `/api/v1/configs/${verticalKey}/${configType}/draft`
      );
      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    enabled: !!verticalKey && !!configType,
  });

  // Parse fetched JSON content into a typed object
  const parsedValue = useMemo(() => {
    if (!data?.content) return null;
    try {
      return JSON.parse(data.content) as ConfigValue;
    } catch {
      return null;
    }
  }, [data?.content]);

  // Initialize edited value when data loads
  useEffect(() => {
    if (parsedValue && editedValue === null) {
      setEditedValue(parsedValue);
    }
  }, [parsedValue]); // deliberately exclude editedValue to avoid resetting edits

  // Local dirty check: compare current edits to original parsed value
  const isDirty = useMemo(() => {
    if (!parsedValue || !editedValue) return false;
    return JSON.stringify(parsedValue) !== JSON.stringify(editedValue);
  }, [parsedValue, editedValue]);

  // Build changed files summary for PromotionDialog
  const changedFiles: readonly ChangedFileSummary[] = useMemo(() => {
    if (!diffResult) return [];
    return [
      {
        configType: apiConfigType,
        diff: diffResult,
        validation: validationResult ?? undefined,
        liveVersion: diffLiveVersion,
        draftVersion: diffDraftVersion ?? data?.version ?? 0,
      },
    ];
  }, [
    diffResult,
    apiConfigType,
    validationResult,
    diffLiveVersion,
    diffDraftVersion,
    data?.version,
  ]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleSave = useCallback((): void => {
    if (editedValue && canEdit) {
      markDirty();
      void saveNow();
    }
  }, [editedValue, canEdit, markDirty, saveNow]);

  const handleOpenPromotionDialog = useCallback((): void => {
    refreshDiff();
    setShowPromotionDialog(true);
  }, [refreshDiff]);

  const handleClosePromotionDialog = useCallback((): void => {
    setShowPromotionDialog(false);
  }, []);

  const handlePromote = useCallback(
    async (changeSummary: string): Promise<void> => {
      await promote(changeSummary);
      queryClient.invalidateQueries({
        queryKey: ["config", verticalKey, configType],
      });
    },
    [promote, queryClient, verticalKey, configType]
  );

  const handleRequestApproval = useCallback(
    async (changeSummary: string): Promise<void> => {
      await approvalApiClient.requestPromotion({
        verticalKey: apiVerticalKey,
        configTypes: [apiConfigType as ApprovalConfigType],
        sourceEnvironment: currentEnvironment as SourceEnvironment,
        targetEnvironment,
        sourceVersion: data?.version ?? 0,
        changeSummary,
      });
    },
    [
      approvalApiClient,
      apiVerticalKey,
      apiConfigType,
      currentEnvironment,
      targetEnvironment,
      data?.version,
    ]
  );

  const handleValidate = useCallback(async (): Promise<void> => {
    await validate();
  }, [validate]);

  const handleToggleHistory = useCallback((): void => {
    if (!showHistoryPanel) {
      refreshHistory();
    }
    setShowHistoryPanel((prev) => !prev);
  }, [showHistoryPanel, refreshHistory]);

  const handleCloseHistory = useCallback((): void => {
    setShowHistoryPanel(false);
  }, []);

  const handleRequestRollback = useCallback((version: number): void => {
    setRollbackTargetVersion(version);
    setShowRollbackDialog(true);
  }, []);

  const handleRollback = useCallback(
    async (reason: string): Promise<void> => {
      if (rollbackTargetVersion === null) return;
      await rollback(rollbackTargetVersion, reason);
      await refreshHistory();
      queryClient.invalidateQueries({
        queryKey: ["config", verticalKey, configType],
      });
      setShowRollbackDialog(false);
      setRollbackTargetVersion(null);
    },
    [rollbackTargetVersion, rollback, refreshHistory, queryClient, verticalKey, configType]
  );

  const handleCloseRollbackDialog = useCallback((): void => {
    setShowRollbackDialog(false);
    setRollbackTargetVersion(null);
  }, []);

  const handleDownload = useCallback(async (): Promise<void> => {
    if (!verticalKey || !configType) return;
    setIsDownloading(true);
    try {
      const env = currentEnvironment ?? "dev";
      const response = await apiRequest<{
        data: unknown;
        meta: {
          verticalKey: string;
          configType: string;
          environment: string;
          exportedAt: string;
          version: string | null;
        };
      }>(`/api/v1/configs/${verticalKey}/${configType}/export?env=${env}`);

      if (!response.success) {
        throw new Error(response.error.message);
      }

      const exportData = response.data;
      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${verticalKey}-${configType}-${env}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to download config";
      window.alert(message);
    } finally {
      setIsDownloading(false);
    }
  }, [verticalKey, configType, currentEnvironment]);

  // Handle value changes from ConfigEditor
  const handleEditorChange = useCallback(
    (newValue: ConfigValue) => {
      setEditedValue(newValue);
      markDirty();
    },
    [markDirty]
  );

  // Keyboard shortcut for save (Ctrl+S / Cmd+S)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (isDirty && canEdit) {
          handleSave();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isDirty, canEdit, handleSave]);

  if (!verticalKey || !configType) {
    return (
      <div className="flex h-full items-center justify-center text-foreground-muted">
        Invalid configuration path
      </div>
    );
  }

  const configLabel = CONFIG_TYPE_LABELS[configType as ConfigType] ?? configType;

  // Effective save status from draft management hook
  const effectiveSaveStatus = draftSaveStatus;

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border bg-background-secondary px-4 py-2">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm">
          <Link
            to="/studio"
            className="flex items-center gap-1 text-foreground-muted hover:text-foreground"
          >
            <Home className="h-4 w-4" />
            <span>Studio</span>
          </Link>
          <ChevronRight className="h-4 w-4 text-foreground-muted" />
          <Link
            to={`/studio/${verticalKey}`}
            className="text-foreground-muted hover:text-foreground"
          >
            {verticalKey}
          </Link>
          <ChevronRight className="h-4 w-4 text-foreground-muted" />
          <span className="font-medium">{configLabel}</span>
          {isDirty && (
            <span className="ml-2 inline-flex items-center rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
              Unsaved
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            className="btn btn-ghost"
            title="Download config"
            onClick={handleDownload}
            disabled={isDownloading}
            data-testid="download-button"
          >
            {isDownloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
          </button>
          <button
            className="btn btn-ghost"
            title="View history"
            onClick={handleToggleHistory}
            data-testid="history-button"
          >
            <History className="h-4 w-4" />
          </button>
          <button
            onClick={handleSave}
            disabled={!isDirty || !canEdit || draftSaveStatus === "saving"}
            className="btn btn-secondary"
          >
            {draftSaveStatus === "saving" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save
          </button>
          {/* Publish / Request Approval button — hidden for viewers, disabled from prod */}
          {(canWrite || canPromoteDirectly) && canPromoteFromEnv ? (
            <button
              onClick={handleOpenPromotionDialog}
              disabled={isDirty || isPromoting}
              className="btn btn-primary"
              data-testid={canPromoteDirectly ? "publish-button" : "request-approval-trigger"}
            >
              {isPromoting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : canPromoteDirectly ? (
                <Upload className="mr-2 h-4 w-4" />
              ) : (
                <ShieldCheck className="mr-2 h-4 w-4" />
              )}
              {canPromoteDirectly ? "Publish" : "Request Approval"}
            </button>
          ) : null}
        </div>
      </div>

      {/* Pending approval banner */}
      {hasPendingApproval && !isLoadingPending && (
        <div
          data-testid="pending-approval-banner"
          className="flex items-center gap-3 border-b border-warning/30 bg-warning/10 px-4 py-2"
        >
          <Clock className="h-4 w-4 text-warning" />
          <span className="text-sm text-warning">
            Pending approval request for this configuration
          </span>
          <a
            href="/studio/approvals"
            className="inline-flex items-center gap-1 text-sm font-medium text-warning hover:text-warning/80"
          >
            View queue
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}

      {/* Editor content */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState
            message={error instanceof Error ? error.message : "Failed to load config"}
            onRetry={refetch}
          />
        ) : editedValue !== null ? (
          <ConfigEditor
            value={editedValue}
            onChange={handleEditorChange}
            configType={configType as EditorConfigType}
            defaultMode="form"
            readOnly={isReadOnly}
          />
        ) : (
          <ErrorState message="Failed to parse configuration content" onRetry={refetch} />
        )}
      </div>

      {/* Status bar */}
      <div className="flex items-center justify-between border-t border-border bg-background-secondary px-4 py-1 text-xs text-foreground-muted">
        <div className="flex items-center gap-4">
          <span>JSON</span>
          {data && (
            <>
              <span>Version {data.version}</span>
              <span className="capitalize">{data.status}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-4">
          <LockIndicatorCompact status={lockStatus} heldByInfo={heldByInfo} />
          <SaveStatusCompact
            status={effectiveSaveStatus}
            isDirty={isDirty || draftIsDirty}
            lastSavedAt={lastSavedAt}
            error={saveError}
          />
        </div>
      </div>

      {/* Promotion Dialog */}
      <PromotionDialog
        isOpen={showPromotionDialog}
        onClose={handleClosePromotionDialog}
        verticalKey={verticalKey}
        configType={apiConfigType}
        changedFiles={changedFiles}
        validationStatus={validationResult}
        isValidating={isValidating}
        onPromote={handlePromote}
        onValidate={handleValidate}
        mode={promotionMode}
        onRequestApproval={handleRequestApproval}
      />

      {/* Version History Panel */}
      <VersionHistoryPanel
        entries={historyEntries}
        isLoading={isLoadingHistory}
        error={historyError}
        liveVersion={liveVersion ?? undefined}
        pagination={historyPagination}
        isOpen={showHistoryPanel}
        onClose={handleCloseHistory}
        onLoadMore={loadMoreHistory}
        onRefresh={refreshHistory}
        onRollback={handleRequestRollback}
      />

      {/* Rollback Dialog */}
      {rollbackTargetVersion !== null && (
        <RollbackDialog
          isOpen={showRollbackDialog}
          onClose={handleCloseRollbackDialog}
          verticalKey={verticalKey}
          configType={apiConfigType}
          targetVersion={rollbackTargetVersion}
          currentVersion={liveVersion ?? 0}
          onRollback={handleRollback}
          isRollingBack={isRollingBack}
        />
      )}
    </div>
  );
}

function LoadingState(): ReactElement {
  return (
    <div className="flex h-full flex-col items-center justify-center text-foreground-muted">
      <Loader2 className="h-8 w-8 animate-spin" />
      <p className="mt-4">Loading configuration...</p>
    </div>
  );
}

interface ErrorStateProps {
  readonly message: string;
  readonly onRetry: () => void;
}

function ErrorState({ message, onRetry }: ErrorStateProps): ReactElement {
  return (
    <div className="flex h-full flex-col items-center justify-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
        <AlertCircle className="h-6 w-6 text-error" />
      </div>
      <p className="mt-4 text-foreground-muted">{message}</p>
      <button onClick={onRetry} className="btn btn-secondary mt-4">
        <RefreshCw className="mr-2 h-4 w-4" />
        Try again
      </button>
    </div>
  );
}
